import { ActionTree } from 'vuex';
import { RootState, SingleFilter } from '@/store/types';
import api from '@/api';
import * as brandRequests from '@/api/requests/profile/brands';
import * as companiesRequests from '@/api/requests/profile/companies';
import * as profileRequests from '@/api/requests/profile/profiles';
import * as profileCategoryRequest from '@/api/requests/profile/profilecategories';
import { BrandResource, ProfileCategoryResource, CompanyResource } from '@/api/requests/profile/types';
import { ProfileState } from './types';
import {
    ProfileMediaCreateRequestPayload,
    ProfileMediaDeleteRequestPayload,
    ProfileMediaUpdateRequestPayload
} from '@/store/modules/profile/types';
import { MediaResource } from '@/api/requests/types';

export const actions: ActionTree<ProfileState, RootState> = {

    /** Profiles */

    /**
     * Fetch all profiles.
     *
     * @param commit
     * @param getters
     */
    async fetchProfiles({ commit, getters }) {
        const response = await api.call(profileRequests.ALL(getters.httpFilters));
        commit('setProfiles', response.data.data);
    },

    /** Brands */

    /**
     * Fetch all brands.
     *
     * @param commit
     * @param getters
     */
    async fetchBrands({ commit, getters }) {
        const response = await api.call(brandRequests.ALL(getters.httpFilters));
        commit('setBrands', response.data.data);
    },

    /**
     * Create a new brand.
     *
     * @param commit
     * @param brand
     */
    async createBrand({ commit }, brand: BrandResource) {
        const response = await api.call(brandRequests.STORE(brand));
        const resource: BrandResource = response.data.data;

        commit('addBrand', resource);
    },

    /**
     * Delete brand by id.
     *
     * @param commit
     * @param brandIds
     */
    async deleteBrand({ commit }, ...brandIds: number[]) {
        await Promise.all(brandIds.map(async (brandId) => {
            await api.call(brandRequests.DELETE(brandId));
            commit('removeBrand', brandId);
        }));
    },

    /**
     * Update an existing brand.
     *
     * @param commit
     * @param brand
     */
    async updateBrand({ commit }, brand: BrandResource) {
        if (!brand.id) {
            throw new Error('No brand id provided.');
        }
        const response = await api.call(brandRequests.UPDATE(brand.id, brand));
        const resource: BrandResource = response.data.data;

        commit('updateBrand', resource);
    },

    /** Profile categories */

    /**
     * Fetch all profile categories.
     *
     * @param commit
     * @param getters
     */
    async fetchProfileCategories({ commit, getters }) {
        const response = await api.call(profileCategoryRequest.ALL(getters.httpFilters));
        commit('setProfileCategories', response.data.data);
    },

    /**
     * Create a new profileCategory.
     *
     * @param commit
     * @param profileCategory
     */
    async createProfileCategory({ commit }, profileCategory: ProfileCategoryResource) {
        const response = await api.call(profileCategoryRequest.STORE(profileCategory));
        const resource: ProfileCategoryResource = response.data.data;

        commit('addProfileCategory', resource);
    },

    /**
     * Delete profileCategory by id.
     *
     * @param commit
     * @param profileCategoryIds
     */
    async deleteCategory({ commit }, ...profileCategoryIds: number[]) {
        await Promise.all(profileCategoryIds.map(async (profileCategoryId) => {
            await api.call(profileCategoryRequest.DELETE(profileCategoryId));
            commit('removeCategory', profileCategoryId);
        }));
    },

    /**
     * Update an existing profileCategory.
     *
     * @param commit
     * @param profileCategory
     */
    async updateCategory({ commit }, profileCategory: ProfileCategoryResource) {
        if (!profileCategory.id) {
            throw new Error('No profileCategory id provided.');
        }
        const response = await api.call(profileCategoryRequest.UPDATE(profileCategory.id, profileCategory));
        const resource: ProfileCategoryResource = response.data.data;

        commit('updateCategory', resource);
    },

    /** Companies */

    /**
     * Fetch all companies.
     */
    async fetchCompanies({ commit }) {
        const response = await api.call(companiesRequests.ALL());
        commit('setCompanies', response.data.data);
    },

    /**
     * Fetch users of a company.
     */
    async fetchCompanyUsers({ dispatch }) {
        const response = await api.call(companiesRequests.ALL());
        dispatch('users/setUsers', response.data.data);
    },

    /**
     * Create a new company.
     *
     * @param commit
     * @param company
     */
    async createCompany({ commit }, company: CompanyResource) {
        const response = await api.call(companiesRequests.SAVE(company));
        commit('addCompany', response.data.data);
        return response.data.data.id;
    },

    /**
     * Update an existing company.
     *
     * @param commit
     * @param company
     */
    async updateCompany({ commit }, company: CompanyResource) {
        if (!company.id) {
            throw new Error('No company id provided');
        }
        const response = await api.call(companiesRequests.UPDATE(company.id, company));
        commit('addCompany', response.data.data);
    },

    /** Filters */

    /**
     * Reset a single filter.
     *
     * @param commit
     * @param key
     */
    resetFilter({ commit }, key: string) {
        commit('resetFilter', key);
    },


    /**
     * Set filters.
     *
     * @param commit
     * @param dispatch
     * @param filters
     */
    setFilters({ commit, dispatch }, filters: SingleFilter[]) {
        filters.map((filter) => commit('setFilter', filter));
    },

    /**
     * Fetch single profile.
     *
     * @param commit
     * @param getters
     */
    async fetchProfile({ commit, getters }, profileId: number) {
        const response = await api.call(profileRequests.FIND(profileId));
        commit('setSelectedProfile', response.data.data);
    },

    /**
     * Add a media to a profile.
     *
     * @param commit
     * @param profileId
     * @param file
     */
    async addMediaToProfile({ commit }, { profileId, file }: ProfileMediaCreateRequestPayload) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.call(profileRequests.ADD_MEDIA(profileId, formData, (progressEvent) => {
            // TODO: gestione progress upload.
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }));
        commit('addMediaToProfile', { profileId, media: response.data.data });
    },

    /**
     * Update a media into a profile.
     *
     * @param commit
     * @param profileId
     * @param mediaId
     * @param media
     */
    async updateMediaIntoProfile({ commit }, { profileId, mediaId, media }: ProfileMediaUpdateRequestPayload) {
        const response = await api.call(profileRequests.UPDATE_MEDIA(profileId, mediaId, media));
        commit('addMediaToProfile', { profileId, media: response.data.data });
    },

    /**
     * Remove a media from a profile.
     *
     * @param commit
     * @param profileId
     * @param mediaId
     */
    async removeMediaFromProfile({ commit }, { profileId, mediaId }: ProfileMediaDeleteRequestPayload) {
        await api.call(profileRequests.REMOVE_MEDIA(profileId, mediaId));
        commit('removeMediaFromProfile', { profileId, mediaId });
    },

    /**
     * Download a media of a profile.
     *
     * @param commit
     * @param getters
     * @param profileId
     * @param mediaId
     */
    async downloadMediaOfProfile({ state, commit, getters }, { profileId, mediaId }: ProfileMediaDeleteRequestPayload) {
        if (!state.selectedProfile) {
            throw new Error('No profile selected');
        }

        // Retrieve media
        const media = state.selectedProfile.media.find((item: MediaResource) => item.id === mediaId);

        if (!media) {
            throw new Error('No media to download');
        }

        const response = await api.call(profileRequests.DOWNLOAD_MEDIA(profileId, mediaId));
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', media.fileName);
        document.body.appendChild(link);
        link.click();
    },

};
